import { Order, OrderSummary } from '../types/order';

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api`;

// Simple cache to prevent duplicate requests
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 30000; // 30 seconds

const getCachedData = (key: string) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  cache.set(key, { data, timestamp: Date.now() });
};

const clearCacheForUser = (userId: number) => {
  const keysToDelete: string[] = [];
  for (const key of cache.keys()) {
    if (key.includes(`_${userId}`)) {
      keysToDelete.push(key);
    }
  }
  keysToDelete.forEach(key => cache.delete(key));
};

export const userOrdersApi = {
  // Kullanıcının tüm siparişlerini getir
  async getUserOrders(userId: number): Promise<Order[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/user/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Siparişler alınamadı');
      }

      return await response.json();
    } catch (error) {
      console.error('Kullanıcı siparişleri getirme hatası:', error);
      throw new Error('Siparişleriniz yüklenirken bir hata oluştu');
    }
  },

  // Kullanıcının son siparişlerini getir (özet halinde)
  async getUserOrdersSummary(userId: number, limit: number = 5): Promise<OrderSummary[]> {
    const cacheKey = `summary_${userId}_${limit}`;
    const cachedSummary = getCachedData(cacheKey);

    if (cachedSummary) {
      return cachedSummary;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/orders/user/${userId}/summary?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Sipariş özeti alınamadı');
      }

      const data = await response.json();
      setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Kullanıcı sipariş özeti getirme hatası:', error);
      throw new Error('Sipariş özetiniz yüklenirken bir hata oluştu');
    }
  },

  // Belirli bir siparişin detayını getir
  async getOrderDetail(orderId: number): Promise<Order> {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/${orderId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Sipariş detayı alınamadı');
      }

      return await response.json();
    } catch (error) {
      console.error('Sipariş detayı getirme hatası:', error);
      throw new Error('Sipariş detayı yüklenirken bir hata oluştu');
    }
  },

  // Kullanıcının sipariş istatistiklerini getir
  async getUserOrderStats(userId: number): Promise<{
    total_orders: number;
    pending_orders: number;
    preparing_orders: number;
    on_the_way_orders: number;
    completed_orders: number;
    total_spent: number;
  }> {
    const cacheKey = `stats_${userId}`;
    const cachedStats = getCachedData(cacheKey);

    if (cachedStats) {
      return cachedStats;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/orders/user/${userId}/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Sipariş istatistikleri alınamadı');
      }

      const data = await response.json();
      setCachedData(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Kullanıcı sipariş istatistikleri getirme hatası:', error);
      throw new Error('Sipariş istatistikleriniz yüklenirken bir hata oluştu');
    }
  },

  // Cache temizleme fonksiyonu
  clearUserCache(userId: number) {
    clearCacheForUser(userId);
  },
};
